import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Loader2, IndianRupee, PlayCircle, XOctagon } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { adminApi, formatApiErrorDetail } from "../lib/authApi";
import { RfqStatusBadge } from "./RfqSection";

const fmtDate = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
const fmtINR = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const QuoteForm = ({ rfq, onQuoted }) => {
  const mid = rfq.estimate_min != null ? Math.round((rfq.estimate_min + rfq.estimate_max) / 2) : "";
  const [price, setPrice] = useState(String(mid));
  const [lead, setLead] = useState("14");
  const [validDays, setValidDays] = useState("15");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const send = async () => {
    const p = parseFloat(price);
    const l = parseInt(lead, 10);
    if (!(p > 0) || !(l >= 1)) {
      toast.error("Enter a valid price and lead time.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await adminApi.post(`/admin/rfqs/${rfq.id}/quote`, {
        price: p, lead_time_days: l, valid_days: parseInt(validDays, 10) || 15, admin_notes: notes,
      });
      toast.success("Quote sent to customer.");
      onQuoted(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 p-5 rounded-lg bg-muted/40 border border-border space-y-4" data-testid={`admin-rfq-quote-form-${rfq.id}`}>
      {rfq.estimate_min != null && (
        <p className="text-xs text-muted-foreground">
          Auto-estimate suggestion: <b className="font-mono-tech text-primary">{fmtINR(rfq.estimate_min)} – {fmtINR(rfq.estimate_max)}</b> (final price is yours to set)
        </p>
      )}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Final Price (INR) *</Label>
          <Input type="number" min="1" value={price} onChange={(e) => setPrice(e.target.value)} data-testid={`admin-rfq-price-input-${rfq.id}`} />
        </div>
        <div className="space-y-2">
          <Label>Lead Time (days) *</Label>
          <Input type="number" min="1" value={lead} onChange={(e) => setLead(e.target.value)} data-testid={`admin-rfq-lead-input-${rfq.id}`} />
        </div>
        <div className="space-y-2">
          <Label>Validity (days)</Label>
          <Input type="number" min="1" value={validDays} onChange={(e) => setValidDays(e.target.value)} data-testid={`admin-rfq-valid-input-${rfq.id}`} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Quote Notes</Label>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Terms, exclusions..." data-testid={`admin-rfq-quote-notes-${rfq.id}`} />
      </div>
      <Button size="sm" disabled={saving} onClick={send} className="rounded-full px-6" data-testid={`admin-rfq-send-quote-btn-${rfq.id}`}>
        {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <IndianRupee className="h-4 w-4 mr-1.5" strokeWidth={1.5} />}
        Send Quote
      </Button>
    </div>
  );
};

export const AdminRfqSection = ({ rfqs, setRfqs, onDownloadFile, downloading }) => {
  const [acting, setActing] = useState(null);

  const upsert = (rfq) => setRfqs((prev) => prev.map((r) => (r.id === rfq.id ? rfq : r)));

  const setStatus = async (rfq, status) => {
    setActing(rfq.id);
    try {
      await adminApi.patch(`/admin/rfqs/${rfq.id}/status`, { status });
      upsert({ ...rfq, status });
      toast.success(`RFQ moved to ${status}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  if (rfqs.length === 0) {
    return <p className="text-sm text-muted-foreground py-8" data-testid="admin-rfqs-empty">No RFQs yet. Customer-submitted RFQs will appear here.</p>;
  }

  return (
    <div className="space-y-4" data-testid="admin-rfq-section">
      {rfqs.map((rfq) => (
        <div key={rfq.id} className="p-6 rounded-xl bg-card border border-border" data-testid={`admin-rfq-card-${rfq.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="font-mono-tech text-sm font-bold text-primary">{rfq.rfq_number}</span>
              <h4 className="font-semibold font-heading">{rfq.title}</h4>
              <RfqStatusBadge status={rfq.status} />
            </div>
            <span className="text-xs text-muted-foreground">{fmtDate(rfq.created_at)}</span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {rfq.name || "—"} · {rfq.email}{rfq.company ? ` · ${rfq.company}` : ""}
          </p>
          {rfq.notes && <p className="mt-2 text-sm">{rfq.notes}</p>}

          <div className="mt-4 space-y-2">
            {rfq.parts.map((p, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-lg bg-muted/40 border border-border text-sm" data-testid={`admin-rfq-part-${rfq.id}-${i}`}>
                <span className="font-medium">{p.name}</span>
                <Badge variant="secondary">{p.process}</Badge>
                <Badge variant="outline">{p.material}{p.grade ? ` · ${p.grade}` : ""}</Badge>
                <span className="text-xs text-muted-foreground">× {p.quantity}</span>
                {p.notes && <span className="text-xs text-muted-foreground">— {p.notes}</span>}
                <span className="ml-auto flex gap-2">
                  {(p.files || []).map((f) => (
                    <button key={f.id} onClick={() => onDownloadFile(f)} disabled={downloading === f.id}
                      data-testid={`admin-rfq-file-download-${f.id}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-medium text-primary hover:bg-primary/20 transition-colors duration-200">
                      {downloading === f.id ? <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} /> : <Download className="h-3 w-3" strokeWidth={1.5} />}
                      {f.name}
                    </button>
                  ))}
                </span>
              </div>
            ))}
          </div>

          {rfq.estimate_min != null && rfq.status !== "QUOTED" && !rfq.quote && (
            <p className="mt-3 font-mono-tech text-sm font-bold text-primary">
              Auto-estimate: {fmtINR(rfq.estimate_min)} – {fmtINR(rfq.estimate_max)}
            </p>
          )}

          {rfq.quote && (
            <div className="mt-4 p-4 rounded-lg bg-violet-500/5 border border-violet-500/20 text-sm" data-testid={`admin-rfq-quote-detail-${rfq.id}`}>
              <span className="font-mono-tech text-lg font-bold">{fmtINR(rfq.quote.price)}</span>
              <span className="ml-4 text-muted-foreground">Lead: {rfq.quote.lead_time_days}d · Valid until {new Date(rfq.quote.valid_until).toLocaleDateString("en-IN")}</span>
              {rfq.quote.admin_notes && <p className="mt-1 text-muted-foreground">{rfq.quote.admin_notes}</p>}
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-3">
            {(rfq.product_understanding || rfq.parts.some(p => p.file_ids?.length)) && <Button asChild size="sm" variant="outline" data-testid={`admin-rfq-understanding-${rfq.id}`}><Link to={`/admin/rfqs/${rfq.id}/understanding`}>Engineering review</Link></Button>}
            {rfq.status === "SUBMITTED" && (
              <Button size="sm" variant="outline" disabled={acting === rfq.id} onClick={() => setStatus(rfq, "QUOTING")} className="rounded-full" data-testid={`admin-rfq-quoting-btn-${rfq.id}`}>
                <PlayCircle className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Start Quoting
              </Button>
            )}
            {!["CLOSED", "DRAFT"].includes(rfq.status) && (
              <Button size="sm" variant="ghost" disabled={acting === rfq.id} onClick={() => setStatus(rfq, "CLOSED")} className="rounded-full text-muted-foreground" data-testid={`admin-rfq-close-btn-${rfq.id}`}>
                <XOctagon className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Close
              </Button>
            )}
          </div>

          {["SUBMITTED", "QUOTING"].includes(rfq.status) && <QuoteForm rfq={rfq} onQuoted={upsert} />}

          {rfq.timeline?.length > 0 && (
            <div className="mt-4 pt-3 border-t border-border flex flex-wrap gap-x-4 gap-y-1">
              {rfq.timeline.map((t, i) => (
                <span key={i} className="text-xs text-muted-foreground">
                  <b>{t.status}</b> · {t.by} · {fmtDate(t.ts)}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
